import { Drawer, Divider } from "@octopusdeploy/design-system-components";
import { DebouncedTextWithLabel } from "@octopusdeploy/design-system-octopus-components";
import { cloneDeep } from "lodash";
import React, { useEffect } from "react";
import type { DeepPartial } from "redux";
import DynamicConnectivityCheck from "~/components/DynamicForm/DynamicConnectivityCheck";
import { ExpandableFormSection, Sensitive, Summary } from "~/components/form";
import FormSectionHeading from "~/components/form/Sections/FormSectionHeading";
import type { ServiceNowIntegrationConnection } from "./ServiceNowIntegrationSettingsPage";
export interface ServiceNowIntegrationDrawerProps {
    connection: ServiceNowIntegrationConnection | undefined;
    connectivityTestUrl: string | undefined;
    isOpen: boolean;
    isEdit: boolean;
    isDisabled?: boolean;
    onClose: () => void;
    onSuccess: (result: ServiceNowIntegrationConnection) => void;
}
interface ConnectivityTestPayload {
    BaseUrl?: string;
    Id?: string;
    OAuthClientId?: string;
    OAuthClientSecret?: string;
    Username?: string;
    UserPassword?: string;
}
const emptyConnection: ServiceNowIntegrationConnection = {
    Id: undefined,
    ConnectionName: undefined,
    BaseUrl: undefined,
    OAuthClientId: undefined,
    OAuthClientSecret: {
        HasValue: false,
        NewValue: undefined,
    },
    Username: undefined,
    UserPassword: {
        HasValue: false,
        NewValue: undefined,
    },
};
export function ServiceNowIntegrationDrawer({ connection, connectivityTestUrl, isOpen, isEdit, isDisabled, onClose, onSuccess }: ServiceNowIntegrationDrawerProps) {
    const [state, setState] = React.useState<ServiceNowIntegrationConnection>(() => cloneDeep(connection ?? emptyConnection));
    const [connectivityTestPayload, setConnectivityTestPayload] = React.useState<ConnectivityTestPayload>({});
    useEffect(() => {
        if (isOpen) {
            setState(cloneDeep(connection ?? emptyConnection));
            setConnectivityTestPayload({
                Id: connection?.Id,
                BaseUrl: connection?.BaseUrl,
                OAuthClientId: connection?.OAuthClientId,
                OAuthClientSecret: connection?.OAuthClientSecret?.NewValue,
                Username: connection?.Username,
                UserPassword: connection?.UserPassword?.NewValue,
            });
        }
    }, [connection, isOpen]);
    const updateConnection = (updated: DeepPartial<ServiceNowIntegrationConnection>) => {
        setState((prev) => ({
            ...prev,
            ...updated,
            OAuthClientSecret: {
                ...prev.OAuthClientSecret,
                ...updated.OAuthClientSecret,
            },
            UserPassword: {
                ...prev.UserPassword,
                ...updated.UserPassword,
            },
        }));
        setConnectivityTestPayload((prev) => ({
            Id: updated.Id ?? prev.Id,
            BaseUrl: updated.BaseUrl ?? prev.BaseUrl,
            OAuthClientId: updated.OAuthClientId ?? prev.OAuthClientId,
            OAuthClientSecret: updated.OAuthClientSecret?.NewValue ?? prev.OAuthClientSecret,
            Username: updated.Username ?? prev.Username,
            UserPassword: updated.UserPassword?.NewValue ?? prev.UserPassword,
        }));
    };
    const onSave = () => onSuccess(state);
    return (<Drawer isOpen={isOpen} onClose={onClose} onPrimaryButtonClick={onSave} isPrimaryButtonDisabled={isDisabled} title={isEdit ? "Edit Connection" : "Add Connection"} variant="default">
            <Divider />
            <FormSectionHeading title="Connection"/>
            <ExpandableFormSection key="ConnectionName" errorKey="ConnectionName" title="ServiceNow Connection Name" help="Enter a name for this connection." isExpandedByDefault={!isEdit} summary={Summary.summary(state.ConnectionName)}>
                <DebouncedTextWithLabel id="ConnectionName" label="ServiceNow Connection Name" value={state.ConnectionName ?? ""} disabled={isDisabled} onChange={(value) => updateConnection({ ConnectionName: value })}/>
            </ExpandableFormSection>
            <ExpandableFormSection key="BaseUrl" errorKey="BaseUrl" title="ServiceNow Base Url" help="Enter the base url of your ServiceNow instance." isExpandedByDefault={!isEdit} summary={Summary.summary(state.BaseUrl)}>
                <DebouncedTextWithLabel id="BaseUrl" label="ServiceNow Base Url" value={state.BaseUrl ?? ""} disabled={isDisabled} onChange={(value) => updateConnection({ BaseUrl: value })}/>
            </ExpandableFormSection>
            <Divider />
            <ExpandableFormSection key="OAuthClientId" errorKey="OAuthClientId" title="ServiceNow OAuth client id" help="Enter the client id generated by your ServiceNow instance." isExpandedByDefault={!isEdit} summary={Summary.summary(state.OAuthClientId)}>
                <DebouncedTextWithLabel id="OAuthClientId" label="ServiceNow OAuth client id" value={state.OAuthClientId ?? ""} disabled={isDisabled} onChange={(value) => updateConnection({ OAuthClientId: value })}/>
            </ExpandableFormSection>
            <ExpandableFormSection key="OAuthClientSecret" errorKey="OAuthClientSecret" title="ServiceNow OAuth client secret" help="Enter the client secret generated by your ServiceNow instance." isExpandedByDefault={!isEdit} summary={Summary.summary(state.OAuthClientSecret?.HasValue ? "******" : "No value provided")}>
                <Sensitive label="ServiceNow OAuth client secret" value={state.OAuthClientSecret} disabled={isDisabled} canRemove={!isDisabled} onChange={(value) => updateConnection({ OAuthClientSecret: value })}/>
            </ExpandableFormSection>
            <ExpandableFormSection key="Username" errorKey="Username" title="ServiceNow Username" help="Enter the username of your ServiceNow service account user." isExpandedByDefault={!isEdit} summary={Summary.summary(state.Username)}>
                <DebouncedTextWithLabel id="Username" label="ServiceNow Username" value={state.Username ?? ""} disabled={isDisabled} onChange={(value) => updateConnection({ Username: value })}/>
            </ExpandableFormSection>
            <ExpandableFormSection key="UserPassword" errorKey="UserPassword" title="ServiceNow Password" help="Enter the password of your ServiceNow service account user." isExpandedByDefault={!isEdit} summary={Summary.summary(state.UserPassword?.HasValue ? "******" : "No value provided")}>
                <Sensitive label="ServiceNow Password" value={state.UserPassword} disabled={isDisabled} canRemove={!isDisabled} onChange={(value) => updateConnection({ UserPassword: value })}/>
                {connectivityTestUrl && <DynamicConnectivityCheck title="ServiceNow connectivity" url={connectivityTestUrl} values={connectivityTestPayload}/>}
            </ExpandableFormSection>
        </Drawer>);
}
